import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from 'src/lib/supabase';
import {
  Sparkles, Wrench, Truck, Heart, Flower2, GraduationCap,
  PartyPopper, ShoppingBag, LayoutGrid, Edit2, Trash2, Plus, X
} from 'lucide-react';

const UI = {
  spacing: { xs: 6, sm: 8, md: 12, lg: 16, xl: 24 },
  radius: { sm: 6, md: 8, lg: 12 },
  colors: {
    primary: "#0F172A",
    secondary: "#334155",
    muted: "#64748B",
    border: "#E2E8F0",
    bg: "#F8FAFC",
    success: "#059669",
    warning: "#D97706",
    danger: "#DC2626",
    info: "#3B82F6"
  }
};

const CATEGORY_ICONS = {
  'cleaning': Sparkles,
  'repairs-maintenance': Wrench,
  'moving-delivery': Truck,
  'personal-home-care': Heart,
  'outdoor-gardening': Flower2,
  'tutoring-learning': GraduationCap,
  'events-catering': PartyPopper,
  'running-errands': ShoppingBag,
};

const pastelColors = ['#FEF3C7', '#DBEAFE', '#D1FAE5', '#FCE7F3', '#EDE9FE', '#FEE2E2', '#E0F2FE', '#F0FDF4'];
const accentColors = ['#D97706', '#3B82F6', '#059669', '#DB2777', '#7C3AED', '#DC2626', '#0284C7', '#16A34A'];

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [newServiceModalOpen, setNewServiceModalOpen] = useState(false);
  const [addingService, setAddingService] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  
  // Form states
  const [newCategory, setNewCategory] = useState({ name: '', icon: '📦', description: '', sort_order: 0 });
  const [newService, setNewService] = useState({ name: '', base_price: '', pricing_type: 'fixed', requires_description: false });

  useEffect(() => {
    loadCategories();
  }, []);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order', { ascending: true });

      if (categoriesError) throw categoriesError;

      setCategories(categoriesData || []);

      // Load services for each category
      for (const cat of categoriesData || []) {
        await loadServicesForCategory(cat.id);
      }
    } catch (error) {
      console.error('Load categories error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadServicesForCategory = async (categoryId) => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('category_id', categoryId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setServices(prev => ({ ...prev, [categoryId]: data || [] }));
    } catch (error) {
      console.error(`Load services error for ${categoryId}:`, error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      showToast('Category name is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const slug = newCategory.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const sort_order = newCategory.sort_order || categories.length;
      
      const { data, error } = await supabase
        .from('categories')
        .insert({
          name: newCategory.name.trim(),
          slug,
          icon: newCategory.icon || '📦',
          description: newCategory.description || null,
          sort_order,
          is_active: true
        })
        .select()
        .single();
      
      if (error) throw error;
      
      setCategories([...categories, data]);
      setModalOpen(false);
      setNewCategory({ name: '', icon: '📦', description: '', sort_order: 0 });
      showToast(`Category "${data.name}" added successfully`);
      await loadServicesForCategory(data.id);
    } catch (error) {
      console.error('Add category error:', error);
      showToast(`Failed to add category: ${error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editFormData.name?.trim()) {
      showToast('Category name is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const slug = editFormData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      
      const { error } = await supabase
        .from('categories')
        .update({
          name: editFormData.name.trim(),
          slug,
          icon: editFormData.icon,
          description: editFormData.description,
          sort_order: editFormData.sort_order,
          is_active: editFormData.is_active
        })
        .eq('id', editFormData.id);
      
      if (error) throw error;
      
      setCategories(prev => prev.map(c => 
        c.id === editFormData.id ? { ...c, ...editFormData, slug } : c
      ));
      
      if (selectedCategory?.id === editFormData.id) {
        setSelectedCategory({ ...selectedCategory, ...editFormData, slug });
      }
      
      setIsEditing(false);
      showToast(`Category "${editFormData.name}" updated successfully`);
    } catch (error) {
      console.error('Update category error:', error);
      showToast(`Failed to update category: ${error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;
    
    if (!window.confirm(`Are you sure you want to delete "${selectedCategory.name}"? This will also delete all services in this category.`)) {
      return;
    }

    setDeleting(true);
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', selectedCategory.id);
      
      if (error) throw error;
      
      setCategories(prev => prev.filter(c => c.id !== selectedCategory.id));
      setDrawerOpen(false);
      setSelectedCategory(null);
      showToast(`Category "${selectedCategory.name}" deleted successfully`);
    } catch (error) {
      console.error('Delete category error:', error);
      showToast(`Failed to delete category: ${error.message}`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (category) => {
    const newActiveState = !category.is_active;
    
    // Optimistic update
    setCategories(prev => prev.map(c => 
      c.id === category.id ? { ...c, is_active: newActiveState } : c
    ));
    
    if (selectedCategory?.id === category.id) {
      setSelectedCategory({ ...selectedCategory, is_active: newActiveState });
    }
    
    try {
      const { error } = await supabase
        .from('categories')
        .update({ is_active: newActiveState })
        .eq('id', category.id);
      
      if (error) throw error;
      
      showToast(`Category ${newActiveState ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Toggle active error:', error);
      // Revert on error
      setCategories(prev => prev.map(c => 
        c.id === category.id ? { ...c, is_active: category.is_active } : c
      ));
      showToast(`Failed to update category status: ${error.message}`, 'error');
    }
  };

  const handleAddService = async () => {
    if (!newService.name.trim()) {
      showToast('Service name is required', 'error');
      return;
    }
    if (!newService.base_price || newService.base_price <= 0) {
      showToast('Valid base price is required', 'error');
      return;
    }

    setAddingService(true);
    try {
      const slug = newService.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const currentServices = services[selectedCategory.id] || [];
      
      const { data, error } = await supabase
        .from('services')
        .insert({
          category_id: selectedCategory.id,
          name: newService.name.trim(),
          slug,
          base_price: parseInt(newService.base_price),
          pricing_type: newService.pricing_type,
          requires_description: newService.requires_description,
          sort_order: currentServices.length
        })
        .select()
        .single();
      
      if (error) throw error;
      
      setServices(prev => ({
        ...prev,
        [selectedCategory.id]: [...(prev[selectedCategory.id] || []), data]
      }));
      
      setNewServiceModalOpen(false);
      setNewService({ name: '', base_price: '', pricing_type: 'fixed', requires_description: false });
      showToast(`Service "${data.name}" added successfully`);
    } catch (error) {
      console.error('Add service error:', error);
      showToast(`Failed to add service: ${error.message}`, 'error');
    } finally {
      setAddingService(false);
    }
  };

  const getCategoryIcon = (slug) => {
    const IconComponent = CATEGORY_ICONS[slug] || LayoutGrid;
    return IconComponent;
  };

  const getIconStyle = (index) => {
    const pastelColor = pastelColors[index % pastelColors.length];
    const accentColor = accentColors[index % accentColors.length];
    return { pastelColor, accentColor };
  };

  const filteredCategories = categories.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalActive = categories.filter(c => c.is_active === true).length;
  const totalInactive = categories.length - totalActive;
  const totalServices = Object.values(services).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 20, marginBottom: UI.spacing.md }}>📊</div>
        <div>Loading categories...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 20, marginBottom: UI.spacing.md }}>⚠️</div>
        <div style={{ fontSize: 16, color: UI.colors.danger, marginBottom: UI.spacing.sm }}>
          Error Loading Data
        </div>
        <div style={{ color: UI.colors.muted, marginBottom: UI.spacing.md }}>{error}</div>
        <button
          onClick={loadCategories}
          style={{
            marginTop: UI.spacing.lg,
            padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`,
            background: UI.colors.primary,
            border: 'none',
            borderRadius: UI.radius.md,
            fontSize: 13,
            fontWeight: 600,
            color: '#FFFFFF',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* KPI Strip */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: UI.spacing.md,
        marginBottom: UI.spacing.xl
      }}>
        {[
          { label: 'Total Categories', value: categories.length },
          { label: 'Active', value: totalActive },
          { label: 'Inactive', value: totalInactive },
          { label: 'Total Services', value: totalServices }
        ].map(kpi => (
          <div key={kpi.label} style={{
            background: '#FFFFFF',
            border: `1px solid ${UI.colors.border}`,
            borderRadius: UI.radius.lg,
            padding: `${UI.spacing.lg}px ${UI.spacing.md}px`
          }}>
            <div style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              color: UI.colors.muted,
              marginBottom: UI.spacing.sm
            }}>
              {kpi.label}
            </div>
            <div style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 28,
              fontWeight: 700,
              color: UI.colors.primary
            }}>
              {kpi.value}
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: UI.spacing.lg,
        gap: UI.spacing.md
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: UI.spacing.sm,
          background: '#FFFFFF',
          border: `1px solid ${UI.colors.border}`,
          borderRadius: UI.radius.md,
          padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
          flex: 1,
          maxWidth: 320
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={UI.colors.muted}>
            <circle cx="10" cy="10" r="7" />
            <line x1="21" y1="21" x2="15" y2="15" />
          </svg>
          <input
            type="text"
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: 13,
              width: '100%',
              background: 'transparent'
            }}
          />
        </div>
        <button
          onClick={() => setModalOpen(true)}
          style={{
            padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`,
            background: UI.colors.primary,
            border: 'none',
            borderRadius: UI.radius.md,
            fontSize: 13,
            fontWeight: 600,
            color: '#FFFFFF',
            cursor: 'pointer'
          }}
        >
          + Add Category
        </button>
      </div>

      {/* Categories Table */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: UI.radius.lg,
        border: `1px solid ${UI.colors.border}`,
        overflow: 'auto'
      }}>
        {filteredCategories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: UI.colors.muted }}>
            No categories found. Click "Add Category" to create one.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: UI.colors.bg, borderBottom: `1px solid ${UI.colors.border}` }}>
                <th style={{ padding: UI.spacing.md, textAlign: 'left', fontSize: 11, fontWeight: 700, color: UI.colors.muted }}>Icon</th>
                <th style={{ padding: UI.spacing.md, textAlign: 'left', fontSize: 11, fontWeight: 700, color: UI.colors.muted }}>Name</th>
                <th style={{ padding: UI.spacing.md, textAlign: 'left', fontSize: 11, fontWeight: 700, color: UI.colors.muted }}>Slug</th>
                <th style={{ padding: UI.spacing.md, textAlign: 'center', fontSize: 11, fontWeight: 700, color: UI.colors.muted }}>Services</th>
                <th style={{ padding: UI.spacing.md, textAlign: 'center', fontSize: 11, fontWeight: 700, color: UI.colors.muted }}>Active</th>
                <th style={{ padding: UI.spacing.md, textAlign: 'center', fontSize: 11, fontWeight: 700, color: UI.colors.muted }}>Sort Order</th>
                <th style={{ padding: UI.spacing.md, textAlign: 'right', fontSize: 11, fontWeight: 700, color: UI.colors.muted }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((category, idx) => {
                const IconComponent = getCategoryIcon(category.slug);
                const { pastelColor, accentColor } = getIconStyle(idx);
                return (
                  <tr
                    key={category.id}
                    style={{
                      borderBottom: `1px solid ${UI.colors.border}`,
                      cursor: 'pointer',
                      transition: 'background 0.1s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = UI.colors.bg}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: UI.spacing.md }}>
                      <div style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: pastelColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <IconComponent size={18} color={accentColor} strokeWidth={1.8} />
                      </div>
                    </td>
                    <td 
                      style={{ padding: UI.spacing.md, fontWeight: 600 }}
                      onClick={() => {
                        setSelectedCategory(category);
                        setDrawerOpen(true);
                      }}
                    >
                      {category.name}
                    </td>
                    <td 
                      style={{ padding: UI.spacing.md, color: UI.colors.muted, fontSize: 12 }}
                      onClick={() => {
                        setSelectedCategory(category);
                        setDrawerOpen(true);
                      }}
                    >
                      {category.slug}
                    </td>
                    <td 
                      style={{ padding: UI.spacing.md, textAlign: 'center' }}
                      onClick={() => {
                        setSelectedCategory(category);
                        setDrawerOpen(true);
                      }}
                    >
                      {services[category.id]?.length || 0}
                    </td>
                    <td style={{ padding: UI.spacing.md, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleToggleActive(category)}
                        style={{
                          padding: `${UI.spacing.xs}px ${UI.spacing.md}px`,
                          background: category.is_active ? UI.colors.success : UI.colors.danger,
                          border: 'none',
                          borderRadius: UI.radius.sm,
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#FFFFFF',
                          cursor: 'pointer',
                          minWidth: 60
                        }}
                      >
                        {category.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td 
                      style={{ padding: UI.spacing.md, textAlign: 'center' }}
                      onClick={() => {
                        setSelectedCategory(category);
                        setDrawerOpen(true);
                      }}
                    >
                      {category.sort_order}
                    </td>
                    <td style={{ padding: UI.spacing.md, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => {
                          setSelectedCategory(category);
                          setDrawerOpen(true);
                        }}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: UI.radius.sm,
                          border: `1px solid ${UI.colors.border}`,
                          background: '#FFFFFF',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <span style={{ fontSize: 20, color: UI.colors.muted }}>→</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Category Modal */}
      {modalOpen && (
        <>
          <div onClick={() => setModalOpen(false)} style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 299
          }} />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 480,
            background: '#FFFFFF',
            borderRadius: UI.radius.lg,
            zIndex: 300,
            padding: UI.spacing.xl
          }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: UI.spacing.lg }}>Add New Category</h3>
            
            <div style={{ marginBottom: UI.spacing.md }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Name *</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., Plumbing"
                style={{
                  width: '100%',
                  padding: UI.spacing.md,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 13
                }}
              />
            </div>
            
            <div style={{ marginBottom: UI.spacing.md }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Icon (Emoji)</label>
              <input
                type="text"
                value={newCategory.icon}
                onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                placeholder="🔧"
                style={{
                  width: '100%',
                  padding: UI.spacing.md,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 20
                }}
              />
            </div>
            
            <div style={{ marginBottom: UI.spacing.md }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Description</label>
              <textarea
                value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                rows={3}
                placeholder="Brief description of this category"
                style={{
                  width: '100%',
                  padding: UI.spacing.md,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 13,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
              />
            </div>
            
            <div style={{ marginBottom: UI.spacing.lg }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Sort Order</label>
              <input
                type="number"
                value={newCategory.sort_order}
                onChange={(e) => setNewCategory({ ...newCategory, sort_order: parseInt(e.target.value) || 0 })}
                style={{
                  width: '100%',
                  padding: UI.spacing.md,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 13
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: UI.spacing.md }}>
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  flex: 1,
                  padding: UI.spacing.md,
                  background: '#FFFFFF',
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: UI.spacing.md,
                  background: UI.colors.primary,
                  border: 'none',
                  borderRadius: UI.radius.md,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#FFFFFF',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                {submitting ? 'Adding...' : 'Add Category'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Category Drawer */}
      {drawerOpen && selectedCategory && (
        <>
          <div onClick={() => setDrawerOpen(false)} style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.35)',
            zIndex: 199
          }} />
          <div style={{
            position: 'fixed',
            right: 0,
            top: 0,
            bottom: 0,
            width: 480,
            background: '#FFFFFF',
            boxShadow: '-4px 0 40px rgba(0,0,0,0.14)',
            zIndex: 200,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: UI.spacing.lg,
              borderBottom: `1px solid ${UI.colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}>
                {selectedCategory.name}
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: UI.radius.sm,
                  border: `1px solid ${UI.colors.border}`,
                  background: '#FFFFFF',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={16} />
              </button>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', padding: UI.spacing.lg }}>
              {!isEditing ? (
                // View Mode
                <>
                  <div style={{ marginBottom: UI.spacing.lg, display: 'flex', alignItems: 'center', gap: UI.spacing.md }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      background: pastelColors[0],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24
                    }}>
                      {selectedCategory.icon || '📦'}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: UI.colors.muted }}>Slug</div>
                      <div style={{ fontSize: 13 }}>{selectedCategory.slug}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: UI.colors.muted }}>Sort Order</div>
                      <div style={{ fontSize: 13 }}>{selectedCategory.sort_order}</div>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: UI.spacing.lg }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: UI.spacing.xs, color: UI.colors.muted }}>Description</div>
                    <div style={{ fontSize: 13 }}>{selectedCategory.description || 'No description'}</div>
                  </div>
                  
                  <div style={{ marginBottom: UI.spacing.lg }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: UI.spacing.sm, color: UI.colors.muted }}>Status</div>
                    <button
                      onClick={() => handleToggleActive(selectedCategory)}
                      style={{
                        padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`,
                        background: selectedCategory.is_active ? UI.colors.success : UI.colors.danger,
                        border: 'none',
                        borderRadius: UI.radius.sm,
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#FFFFFF',
                        cursor: 'pointer'
                      }}
                    >
                      {selectedCategory.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                  
                  <div style={{ marginBottom: UI.spacing.lg }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: UI.spacing.sm, color: UI.colors.muted }}>Actions</div>
                    <div style={{ display: 'flex', gap: UI.spacing.sm }}>
                      <button
                        onClick={() => {
                          setEditFormData({
                            id: selectedCategory.id,
                            name: selectedCategory.name,
                            icon: selectedCategory.icon,
                            description: selectedCategory.description || '',
                            sort_order: selectedCategory.sort_order,
                            is_active: selectedCategory.is_active
                          });
                          setIsEditing(true);
                        }}
                        style={{
                          flex: 1,
                          padding: UI.spacing.sm,
                          background: UI.colors.primary,
                          border: 'none',
                          borderRadius: UI.radius.sm,
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#FFFFFF',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: UI.spacing.xs
                        }}
                      >
                        <Edit2 size={14} /> Edit
                      </button>
                      <button
                        onClick={handleDeleteCategory}
                        disabled={deleting}
                        style={{
                          flex: 1,
                          padding: UI.spacing.sm,
                          background: UI.colors.danger,
                          border: 'none',
                          borderRadius: UI.radius.sm,
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#FFFFFF',
                          cursor: deleting ? 'not-allowed' : 'pointer',
                          opacity: deleting ? 0.6 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: UI.spacing.xs
                        }}
                      >
                        <Trash2 size={14} /> {deleting ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // Edit Mode
                <>
                  <div style={{ marginBottom: UI.spacing.md }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Name *</label>
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      style={{
                        width: '100%',
                        padding: UI.spacing.md,
                        border: `1px solid ${UI.colors.border}`,
                        borderRadius: UI.radius.md,
                        fontSize: 13
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: UI.spacing.md }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Icon (Emoji)</label>
                    <input
                      type="text"
                      value={editFormData.icon}
                      onChange={(e) => setEditFormData({ ...editFormData, icon: e.target.value })}
                      style={{
                        width: '100%',
                        padding: UI.spacing.md,
                        border: `1px solid ${UI.colors.border}`,
                        borderRadius: UI.radius.md,
                        fontSize: 20
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: UI.spacing.md }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Description</label>
                    <textarea
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: UI.spacing.md,
                        border: `1px solid ${UI.colors.border}`,
                        borderRadius: UI.radius.md,
                        fontSize: 13,
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: UI.spacing.lg }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Sort Order</label>
                    <input
                      type="number"
                      value={editFormData.sort_order}
                      onChange={(e) => setEditFormData({ ...editFormData, sort_order: parseInt(e.target.value) || 0 })}
                      style={{
                        width: '100%',
                        padding: UI.spacing.md,
                        border: `1px solid ${UI.colors.border}`,
                        borderRadius: UI.radius.md,
                        fontSize: 13
                      }}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', gap: UI.spacing.sm }}>
                    <button
                      onClick={() => setIsEditing(false)}
                      style={{
                        flex: 1,
                        padding: UI.spacing.sm,
                        background: '#FFFFFF',
                        border: `1px solid ${UI.colors.border}`,
                        borderRadius: UI.radius.sm,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateCategory}
                      disabled={submitting}
                      style={{
                        flex: 1,
                        padding: UI.spacing.sm,
                        background: UI.colors.primary,
                        border: 'none',
                        borderRadius: UI.radius.sm,
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#FFFFFF',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        opacity: submitting ? 0.6 : 1
                      }}
                    >
                      {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </>
              )}
              
              <div style={{
                marginTop: UI.spacing.lg,
                paddingTop: UI.spacing.lg,
                borderTop: `1px solid ${UI.colors.border}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: UI.spacing.md }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>Services</div>
                  <button
                    onClick={() => setNewServiceModalOpen(true)}
                    style={{
                      padding: `${UI.spacing.xs}px ${UI.spacing.md}px`,
                      background: UI.colors.primary,
                      border: 'none',
                      borderRadius: UI.radius.sm,
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#FFFFFF',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: UI.spacing.xs
                    }}
                  >
                    <Plus size={12} /> Add Service
                  </button>
                </div>
                
                {services[selectedCategory.id]?.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: UI.spacing.lg, color: UI.colors.muted }}>
                    No services yet. Click "Add Service" to create one.
                  </div>
                ) : (
                  services[selectedCategory.id]?.map(service => (
                    <div key={service.id} style={{
                      padding: UI.spacing.md,
                      background: UI.colors.bg,
                      borderRadius: UI.radius.md,
                      marginBottom: UI.spacing.sm
                    }}>
                      <div style={{ fontWeight: 600 }}>{service.name}</div>
                      <div style={{ fontSize: 12, color: UI.colors.muted }}>
                        KES {service.base_price?.toLocaleString()} · {service.pricing_type === 'hourly' ? 'Hourly' : 'Fixed'}
                        {service.requires_description && ' · Requires description'}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Service Modal */}
      {newServiceModalOpen && (
        <>
          <div onClick={() => setNewServiceModalOpen(false)} style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 499
          }} />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 480,
            background: '#FFFFFF',
            borderRadius: UI.radius.lg,
            zIndex: 500,
            padding: UI.spacing.xl
          }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: UI.spacing.lg }}>Add New Service</h3>
            
            <div style={{ marginBottom: UI.spacing.md }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Service Name *</label>
              <input
                type="text"
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                placeholder="e.g., House Cleaning"
                style={{
                  width: '100%',
                  padding: UI.spacing.md,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 13
                }}
              />
            </div>
            
            <div style={{ marginBottom: UI.spacing.md }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Base Price (KES) *</label>
              <input
                type="number"
                value={newService.base_price}
                onChange={(e) => setNewService({ ...newService, base_price: e.target.value })}
                placeholder="2500"
                style={{
                  width: '100%',
                  padding: UI.spacing.md,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 13
                }}
              />
            </div>
            
            <div style={{ marginBottom: UI.spacing.md }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Pricing Type</label>
              <select
                value={newService.pricing_type}
                onChange={(e) => setNewService({ ...newService, pricing_type: e.target.value })}
                style={{
                  width: '100%',
                  padding: UI.spacing.md,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 13,
                  background: '#FFFFFF'
                }}
              >
                <option value="fixed">Fixed Price</option>
                <option value="hourly">Hourly Rate</option>
              </select>
            </div>
            
            <div style={{ marginBottom: UI.spacing.lg }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: UI.spacing.sm, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newService.requires_description}
                  onChange={(e) => setNewService({ ...newService, requires_description: e.target.checked })}
                />
                <span style={{ fontSize: 13 }}>Requires description from customer</span>
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: UI.spacing.md }}>
              <button
                onClick={() => setNewServiceModalOpen(false)}
                style={{
                  flex: 1,
                  padding: UI.spacing.md,
                  background: '#FFFFFF',
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddService}
                disabled={addingService}
                style={{
                  flex: 1,
                  padding: UI.spacing.md,
                  background: UI.colors.primary,
                  border: 'none',
                  borderRadius: UI.radius.md,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#FFFFFF',
                  cursor: addingService ? 'not-allowed' : 'pointer',
                  opacity: addingService ? 0.6 : 1
                }}
              >
                {addingService ? 'Adding...' : 'Add Service'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: toast.type === 'error' ? UI.colors.danger : UI.colors.success,
          color: '#FFFFFF',
          padding: `${UI.spacing.md}px ${UI.spacing.lg}px`,
          borderRadius: UI.radius.md,
          fontSize: 13,
          fontWeight: 600,
          zIndex: 999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}